import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { useAuth } from '../hooks/useAuth';
import { amazon, dashboard, shipments as shipmentsApi } from '../services/api';
import ProfitCalculator from './ProfitCalculator';
import BSRTracker from './BSRTracker';
import ShipmentETA from './ShipmentETA';
import Reports from './Reports';
import ReorderAlerts from './ReorderAlerts';
import Settings from './Settings';

const fmt = (n) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(n || 0);

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [activeAccount, setActiveAccount] = useState(null);
  const [tab, setTab] = useState('overview');
  const [days, setDays] = useState(30);
  const [summary, setSummary] = useState(null);
  const [dailySales, setDailySales] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [payout, setPayout] = useState(null);
  const [shipmentsList, setShipmentsList] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [showAddShipment, setShowAddShipment] = useState(false);
  const [newShipment, setNewShipment] = useState({});

  useEffect(() => {
    amazon.getAccounts().then(accs => {
      setAccounts(accs);
      if (accs.length > 0) setActiveAccount(accs[0]);
    }).catch(() => {});
  }, []);

  const loadData = useCallback(async (acc, d) => {
    if (!acc) return;
    try {
      const [s, daily, inv, p, sh] = await Promise.all([
        dashboard.summary(acc.id, d),
        dashboard.salesDaily(acc.id, d),
        dashboard.inventory(acc.id),
        dashboard.payout(acc.id),
        shipmentsApi.list(acc.id),
      ]);
      setSummary(s);
      setDailySales(daily.map(row => ({
        ...row,
        date: new Date(row.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
      })));
      setInventory(inv);
      setPayout(p);
      setShipmentsList(sh);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    loadData(activeAccount, days);
  }, [activeAccount, days, loadData]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await dashboard.sync(activeAccount.id);
      await loadData(activeAccount, days);
    } finally {
      setSyncing(false);
    }
  };

  const connectStore = async () => {
    try {
      await amazon.connectSelf('A1F83G8C2ARO7P');
      const accs = await amazon.getAccounts();
      setAccounts(accs);
      if (accs.length > 0) setActiveAccount(accs[0]);
    } catch (e) {
      console.error(e);
    }
  };

  const handleAddShipment = async () => {
    await shipmentsApi.create(activeAccount.id, newShipment);
    setShowAddShipment(false);
    setNewShipment({});
    const sh = await shipmentsApi.list(activeAccount.id);
    setShipmentsList(sh);
  };

  const card = { background: '#fff', border: '0.5px solid rgba(0,0,0,0.08)', borderRadius: '12px', padding: '1.25rem', marginBottom: '12px' };
  const metric = (type) => ({
    background: type === 'pos' ? '#EAF3DE' : type === 'neg' ? '#fff' : '#f0efe9',
    border: '0.5px solid rgba(0,0,0,0.06)', borderRadius: '10px', padding: '14px 16px'
  });
  const badge = (type) => ({
    display: 'inline-block', fontSize: '11px', fontWeight: '500', padding: '2px 8px', borderRadius: '4px',
    background: type === 'ok' ? '#EAF3DE' : type === 'warn' ? '#FAEEDA' : type === 'danger' ? '#FCEBEB' : '#f0efe9',
    color: type === 'ok' ? '#27500A' : type === 'warn' ? '#854F0B' : type === 'danger' ? '#A32D2D' : '#666'
  });

  const tabs = ['overview', 'inventory', 'shipments', 'payout', 'profit', 'bsr', 'eta', 'reports', 'reorder', 'settings'];

  const tabLabel = (t) => {
    if (t === 'bsr') return 'BSR Tracker';
    if (t === 'eta') return 'Shipment ETA';
    if (t === 'reports') return 'P&L Reports';
    if (t === 'reorder') return 'Reorder Alerts';
    if (t === 'settings') return 'Settings';
    if (t === 'profit') return 'Profit Calculator';
    return t.charAt(0).toUpperCase() + t.slice(1);
  };

  return (
    <div style={{ fontFamily: '-apple-system,BlinkMacSystemFont,sans-serif', display: 'flex', minHeight: '100vh' }}>
      <aside style={{ width: '220px', background: '#1a1a1a', color: '#e0e0e0', display: 'flex', flexDirection: 'column', padding: '1.25rem', flexShrink: 0 }}>
        <div style={{ fontSize: '16px', fontWeight: '600', color: '#fff', marginBottom: '1.5rem' }}>SellerDash</div>

        <div style={{ fontSize: '10px', fontWeight: '600', letterSpacing: '.08em', textTransform: 'uppercase', color: '#666', marginBottom: '6px' }}>Stores</div>
        {accounts.map(acc => (
          <div key={acc.id} onClick={() => setActiveAccount(acc)}
            style={{ fontSize: '13px', padding: '7px 10px', borderRadius: '7px', cursor: 'pointer', color: activeAccount?.id === acc.id ? '#fff' : '#bbb', background: activeAccount?.id === acc.id ? 'rgba(255,255,255,.12)' : 'transparent', marginBottom: '2px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#1D9E75', flexShrink: 0, display: 'inline-block' }} />
            {acc.marketplace_name}
          </div>
        ))}
        <button onClick={connectStore} style={{ background: 'transparent', border: '0.5px dashed #444', color: '#888', borderRadius: '7px', padding: '6px 10px', fontSize: '12px', cursor: 'pointer', width: '100%', textAlign: 'left', marginTop: '4px' }}>
          + Connect store
        </button>

        <div style={{ fontSize: '10px', fontWeight: '600', letterSpacing: '.08em', textTransform: 'uppercase', color: '#666', margin: '1.5rem 0 6px' }}>Navigation</div>
        {tabs.map(t => (
          <div key={t} onClick={() => setTab(t)}
            style={{ fontSize: '13px', padding: '7px 10px', borderRadius: '7px', cursor: 'pointer', color: tab === t ? '#fff' : '#bbb', background: tab === t ? 'rgba(255,255,255,.12)' : 'transparent', marginBottom: '2px' }}>
            {tabLabel(t)}
          </div>
        ))}

        <div style={{ marginTop: 'auto', paddingTop: '1rem', borderTop: '0.5px solid #333' }}>
          <div style={{ fontSize: '11px', color: '#555', marginBottom: '6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.email}</div>
          <button onClick={() => { logout(); navigate('/login'); }} style={{ background: 'transparent', border: 'none', color: '#666', fontSize: '12px', cursor: 'pointer', padding: 0 }}>Sign out</button>
        </div>
      </aside>

      <main style={{ flex: 1, padding: '2rem', background: '#f5f4f0', overflowX: 'hidden' }}>
        {tab === 'settings' ? (
          <div>
            <div style={{ fontSize: '20px', fontWeight: '500', marginBottom: '1.5rem' }}>Settings</div>
            <Settings />
          </div>
        ) : !activeAccount ? (
          <div style={{ textAlign: 'center', padding: '4rem 2rem' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '500', marginBottom: '8px' }}>Connect your Amazon store</h2>
            <p style={{ fontSize: '14px', color: '#777', marginBottom: '1.5rem' }}>Link your Seller Central account to start seeing your data.</p>
            <button onClick={connectStore} style={{ background: '#1a1a1a', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 20px', fontSize: '14px', cursor: 'pointer' }}>Connect Amazon store</button>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
              <div>
                <h1 style={{ fontSize: '20px', fontWeight: '500', margin: 0 }}>{activeAccount.marketplace_name}</h1>
                <span style={{ fontSize: '12px', color: '#888' }}>Last synced: {activeAccount.last_synced_at ? new Date(activeAccount.last_synced_at).toLocaleString('en-GB') : 'Never'}</span>
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {tab === 'overview' && (
                  <div style={{ display: 'flex', gap: '4px' }}>
                    {[1, 7, 30].map(d => (
                      <button key={d} onClick={() => setDays(d)}
                        style={{ padding: '6px 12px', borderRadius: '7px', border: '0.5px solid #ddd', background: days === d ? '#1a1a1a' : '#fff', color: days === d ? '#fff' : '#1a1a1a', fontSize: '12px', cursor: 'pointer' }}>
                        {d}d
                      </button>
                    ))}
                  </div>
                )}
                <button onClick={handleSync} disabled={syncing} style={{ background: '#fff', border: '0.5px solid #ddd', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer' }}>
                  {syncing ? 'Syncing...' : 'Sync now'}
                </button>
              </div>
            </div>

            {tab === 'overview' && summary && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '10px', marginBottom: '1.25rem' }}>
                  {[
                    { label: 'Total sales', value: fmt(summary.total_sales), type: '' },
                    { label: 'Units sold', value: parseInt(summary.units_sold).toLocaleString(), type: '' },
                    { label: 'FBA fees (inc. VAT)', value: fmt(parseFloat(summary.fba_fees || 0) + parseFloat(summary.referral_fees || 0)), type: 'neg' },
                    { label: 'VAT due to HMRC', value: fmt(summary.vat), type: 'neg' },
                    { label: 'COGS', value: fmt(summary.cogs), type: 'neg' },
                    { label: 'Net profit', value: fmt(summary.net_profit), type: 'pos' },
                    { label: 'Margin', value: `${summary.margin}%`, type: 'pos' },
                  ].map(m => (
                    <div key={m.label} style={metric(m.type)}>
                      <div style={{ fontSize: '11px', color: '#777', marginBottom: '5px', fontWeight: '500' }}>{m.label}</div>
                      <div style={{ fontSize: '20px', fontWeight: '500', color: m.type === 'pos' ? '#1D9E75' : m.type === 'neg' ? '#E24B4A' : '#1a1a1a' }}>{m.value}</div>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div style={card}>
                    <div style={{ fontSize: '12px', fontWeight: '500', color: '#777', marginBottom: '12px' }}>Daily sales</div>
                    <ResponsiveContainer width="100%" height={180}>
                      <LineChart data={dailySales}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                        <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `£${v}`} />
                        <Tooltip formatter={v => fmt(v)} />
                        <Line type="monotone" dataKey="total_sales" stroke="#378ADD" strokeWidth={2} dot={false} name="Sales" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  <div style={card}>
                    <div style={{ fontSize: '12px', fontWeight: '500', color: '#777', marginBottom: '12px' }}>Daily units sold</div>
                    <ResponsiveContainer width="100%" height={180}>
                      <BarChart data={dailySales}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                        <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Bar dataKey="units_sold" fill="#1D9E75" radius={[3, 3, 0, 0]} name="Units" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </>
            )}

            {tab === 'inventory' && (
              <div style={card}>
                <div style={{ fontSize: '13px', fontWeight: '500', color: '#555', marginBottom: '1rem' }}>Inventory & stock health</div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr>
                      {['SKU / Product', 'Units available', 'Inbound', 'Avg sold/day (7d)', 'Days of stock', 'COGS/unit'].map(h => (
                        <th key={h} style={{ fontSize: '11px', fontWeight: '500', color: '#888', textAlign: 'left', padding: '0 10px 8px', borderBottom: '0.5px solid rgba(0,0,0,0.08)' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {inventory.map(row => (
                      <tr key={row.sku}>
                        <td style={{ padding: '10px', borderBottom: '0.5px solid rgba(0,0,0,0.05)' }}>
                          <div style={{ fontWeight: '500' }}>{row.sku}</div>
                          <div style={{ fontSize: '11px', color: '#999' }}>{row.product_name}</div>
                        </td>
                        <td style={{ padding: '10px', borderBottom: '0.5px solid rgba(0,0,0,0.05)' }}>{row.units_available}</td>
                        <td style={{ padding: '10px', borderBottom: '0.5px solid rgba(0,0,0,0.05)' }}>{row.units_inbound}</td>
                        <td style={{ padding: '10px', borderBottom: '0.5px solid rgba(0,0,0,0.05)' }}>{parseFloat(row.avg_daily_units_7d || 0).toFixed(1)}</td>
                        <td style={{ padding: '10px', borderBottom: '0.5px solid rgba(0,0,0,0.05)' }}>
                          <span style={badge(row.days_of_stock === null ? 'gray' : row.days_of_stock >= 30 ? 'ok' : row.days_of_stock >= 14 ? 'warn' : 'danger')}>
                            {row.days_of_stock === null ? 'No data' : `${row.days_of_stock}d`}
                          </span>
                        </td>
                        <td style={{ padding: '10px', borderBottom: '0.5px solid rgba(0,0,0,0.05)' }}>
                          <input type="number" step="0.01" defaultValue={row.cogs_per_unit || ''} placeholder="£0.00"
                            style={{ width: '80px', padding: '5px 8px', border: '0.5px solid #ddd', borderRadius: '6px', fontSize: '13px' }}
                            onBlur={e => dashboard.updateCogs(activeAccount.id, row.sku, e.target.value)} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {tab === 'shipments' && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <span style={{ fontSize: '13px', fontWeight: '500', color: '#555' }}>Incoming shipments</span>
                  <button onClick={() => setShowAddShipment(true)} style={{ background: '#fff', border: '0.5px solid #ddd', borderRadius: '8px', padding: '7px 14px', fontSize: '13px', cursor: 'pointer' }}>+ Add shipment</button>
                </div>

                {showAddShipment && (
                  <div style={{ ...card, marginBottom: '1rem' }}>
                    <div style={{ fontWeight: '500', marginBottom: '1rem' }}>New shipment</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                      {[['Reference', 'reference', 'text'], ['Product description', 'product_description', 'text'], ['Supplier name', 'supplier_name', 'text'], ['Units', 'units', 'number'], ['Order placed', 'order_placed_at', 'date'], ['Payment cleared', 'payment_cleared_at', 'date'], ['Goods shipped', 'goods_shipped_at', 'date'], ['Expected arrival', 'expected_arrival', 'date']].map(([label, key, type]) => (
                        <div key={key}>
                          <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#666', marginBottom: '4px' }}>{label}</label>
                          <input type={type} style={{ width: '100%', padding: '8px 10px', border: '0.5px solid #ddd', borderRadius: '7px', fontSize: '13px', boxSizing: 'border-box' }}
                            onChange={e => setNewShipment(s => ({ ...s, [key]: e.target.value }))} />
                        </div>
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: '8px', marginTop: '1rem' }}>
                      <button onClick={handleAddShipment} style={{ background: '#1a1a1a', color: '#fff', border: 'none', borderRadius: '8px', padding: '9px 18px', fontSize: '13px', cursor: 'pointer' }}>Save shipment</button>
                      <button onClick={() => setShowAddShipment(false)} style={{ background: '#fff', border: '0.5px solid #ddd', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer' }}>Cancel</button>
                    </div>
                  </div>
                )}

                {shipmentsList.map(sh => (
                  <div key={sh.id} style={card}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                      <div>
                        <div style={{ fontWeight: '500' }}>{sh.product_description || sh.reference}</div>
                        <div style={{ fontSize: '11px', color: '#999' }}>{sh.reference} · {sh.supplier_name} · {sh.units} units</div>
                      </div>
                      <span style={badge(sh.status === 'shipped' ? 'ok' : sh.status === 'paid' ? 'warn' : 'gray')}>{sh.status}</span>
                    </div>
                    <div style={{ display: 'flex' }}>
                      {[
                        { label: 'Order placed', date: sh.order_placed_at, done: !!sh.order_placed_at },
                        { label: 'Payment cleared', date: sh.payment_cleared_at, done: !!sh.payment_cleared_at },
                        { label: 'Goods shipped', date: sh.goods_shipped_at, done: !!sh.goods_shipped_at },
                        { label: 'Expected arrival', date: sh.expected_arrival, done: false }
                      ].map((step, i) => (
                        <div key={i} style={{ flex: 1, textAlign: 'center' }}>
                          <div style={{ width: '16px', height: '16px', borderRadius: '50%', margin: '0 auto 4px', background: step.done ? '#1D9E75' : '#fff', border: `2px solid ${step.done ? '#1D9E75' : '#ddd'}` }} />
                          <div style={{ fontSize: '10px', color: '#999' }}>{step.label}</div>
                          <div style={{ fontSize: '10px', fontWeight: '500', color: '#444' }}>{step.date ? new Date(step.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '—'}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {tab === 'payout' && (
              <div>
                {payout ? (
                  <div style={{ background: '#EAF3DE', borderRadius: '12px', padding: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <div>
                      <div style={{ fontSize: '32px', fontWeight: '500', color: '#27500A' }}>{fmt(payout.estimated_amount)}</div>
                      <div style={{ fontSize: '13px', color: '#3B6D11', marginTop: '4px' }}>Upcoming payout from Amazon</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '14px', fontWeight: '500', color: '#27500A' }}>
                        {payout.expected_date ? new Date(payout.expected_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : 'Date TBC'}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '3rem', color: '#888' }}>
                    No payout data yet — click Sync now to fetch from Amazon.
                  </div>
                )}
              </div>
            )}

            {tab === 'profit' && (
              <div>
                <div style={{ fontSize: '13px', fontWeight: '500', color: '#555', marginBottom: '1rem' }}>Profit & VAT Calculator</div>
                <ProfitCalculator />
              </div>
            )}

            {tab === 'bsr' && (
              <BSRTracker accountId={activeAccount?.id} inventory={inventory} />
            )}

            {tab === 'eta' && (
              <div>
                <div style={{ fontSize: '13px', fontWeight: '500', color: '#555', marginBottom: '1rem' }}>Shipment ETA Calculator</div>
                <ShipmentETA />
              </div>
            )}

            {tab === 'reports' && (
              <div>
                <div style={{ fontSize: '13px', fontWeight: '500', color: '#555', marginBottom: '1rem' }}>P&L Reports</div>
                <Reports accountId={activeAccount?.id} />
              </div>
            )}

            {tab === 'reorder' && (
              <ReorderAlerts accountId={activeAccount?.id} />
            )}
          </>
        )}
      </main>
    </div>
  );
}